// src/components/Navbar.jsx
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AdminLoginModal from './AdminLoginModal';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Capabilities', path: '/capabilities' },
  { label: 'Workforce', path: '/workforce' },
  { label: 'Government & RFPs', path: '/government-rfps' },
  { label: 'Careers', path: '/careers' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

// Logo SVG (District Sourcing Tech icon)
function LogoIcon({ color = '#1840b5' }) {
  // The actual DST logo - a stylized "d" letter
  const bg = color === '#fff' ? '#154DDE' : '#154DDE';
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="16" fill={color === '#fff' ? '#154DDE' : '#154DDE'}/>
      <path d="M20.666 7.407V11.974C20.666 12.076 20.661 12.177 20.652 12.276C20.649 12.31 20.646 12.343 20.641 12.376C20.436 14.066 19.003 15.368 17.262 15.368V10.801C17.262 8.944 18.789 7.407 20.666 7.407Z" fill="white"/>
      <path d="M20.666 24.593L14.601 24.593C11.086 24.593 8.237 21.756 8.237 18.257C8.237 16.507 8.939 14.921 10.08 13.762C11.222 12.609 12.812 11.887 14.577 11.887L16.368 11.89V15.059H14.699C13.875 15.059 13.127 15.394 12.579 15.939C12.021 16.492 11.674 17.258 11.684 18.097C11.702 19.731 13.091 21.053 14.738 21.053H17.58C19.082 21.053 20.343 22.107 20.646 23.522C20.694 23.742 20.719 23.968 20.719 24.2C20.719 24.331 20.701 24.463 20.666 24.593Z" fill="white"/>
      <path d="M20.666 15.983V22.447C20.502 22.253 20.317 22.075 20.116 21.916C19.391 21.33 18.473 20.986 17.513 20.986H17.262V16.734C18.643 16.734 19.882 16.056 20.666 15.983Z" fill="white"/>
    </svg>
  );
}

export default function Navbar({ variant = 'dark' }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showAdminBtn, setShowAdminBtn] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const location = useLocation();

  // 4× 'a' keypress detection for hidden admin trigger
  const pressBuffer = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key.toLowerCase() !== 'a') {
        pressBuffer.current = [];
        return;
      }
      pressBuffer.current.push(Date.now());
      if (pressBuffer.current.length >= 4) {
        setShowAdminBtn(true);
        pressBuffer.current = [];
      }
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => { pressBuffer.current = []; }, 2000);
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isDark = variant === 'dark';
  const navClass = isDark ? 'navbar dark' : 'navbar light';

  return (
    <>
      <nav className={navClass} style={scrolled && !isDark ? { boxShadow: '0 1px 0 var(--shades-400)' } : {}}>
        <div className="navbar__inner">
          {/* Logo */}
          <Link to="/" className="navbar__logo" style={{ textDecoration: 'none' }}>
            <LogoIcon />
            <div className={`navbar__logo-text ${isDark ? 'dark' : 'light'}`}>
              <div>District</div>
              <div>Sourcing Tech</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className={`navbar__links ${menuOpen ? 'open' : ''}`}>
            {NAV_LINKS.map(({ label, path }) => {
              const isActive = location.pathname === path || (path === '/blog' && location.pathname.startsWith('/blog'));
              return (
                <Link
                  key={path}
                  to={path}
                  className={`navbar__link ${isDark ? 'dark' : 'light'} ${isActive ? 'active' : ''}`}
                >
                  {label}
                </Link>
              );
            })}
          </div>

          {/* Mobile hamburger */}
          <button
            className={`navbar__toggle ${isDark ? 'dark' : 'light'}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span style={menuOpen ? { transform: 'rotate(45deg) translate(5px, 5px)' } : {}} />
            <span style={menuOpen ? { opacity: 0 } : {}} />
            <span style={menuOpen ? { transform: 'rotate(-45deg) translate(5px, -5px)' } : {}} />
          </button>
        </div>
      </nav>

      {/* Hidden admin trigger button */}
      {showAdminBtn && (
        <button
          className="admin-trigger-btn"
          onClick={() => { setShowAdminModal(true); setShowAdminBtn(false); }}
          title="Admin"
        >
          ⚙
        </button>
      )}

      {/* Admin login modal */}
      {showAdminModal && (
        <AdminLoginModal onClose={() => setShowAdminModal(false)} />
      )}
    </>
  );
}